import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http, { authHttp } from "../api/http";
import Option from "../types/option";
import User from "../types/user";
import { Applications } from "../types/company";

interface UserState {
  user?: User;
  userList?: User[];
  adminUserList?: User[];
  textUserList?: User[];
  userRoles?: Option[];
  status: "idle" | "loading" | "failed";
  formStatus: "idle" | "loading" | "failed";
}

const initialState: UserState = {
  status: "idle",
  formStatus: "idle",
};

export const fetchUsers = createAsyncThunk<User[]>(
  "users/fetch-users",
  async () => {
    const [profileResponse, dbResponse] = await Promise.all([
      authHttp.get<Partial<User>[]>(`/user?appId=${Applications.Detector}`),
      http.get<Partial<User>[]>(`/user`),
    ]);

    const profileUsers = profileResponse.data ?? [];
    const dbUsers = dbResponse.data ?? [];

    const profileById = new Map<string, Partial<User>>(
      profileUsers.filter((u) => !!u.userId).map((u) => [u.userId as string, u])
    );

    const result: User[] = [];
    for (const db of dbUsers) {
      const id = db.userId as string | undefined;
      if (!id) continue;
      const profile = profileById.get(id);
      if (!profile) continue; // only keep users present in both lists
      result.push({ ...(db as User), ...profile } as User);
    }

    return result;
  }
);

export const addUser = createAsyncThunk<User, User>(
  "users/add-user",
  async (user: User) => {
    const response = await http.post<User>(`/user`, user, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const editUser = createAsyncThunk<User, User>(
  "users/edit-user",
  async (user: User) => {
    const response = await http.put<User>(`/user/${user.detectorId}`, user, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const getUser = createAsyncThunk<User, number>(
  "users/get-user",
  async (id: number) => {
    const response = await http.get<User>(`/user/${id}`);
    return response.data;
  }
);

export const deleteUser = createAsyncThunk<null, number>(
  "users/delete",
  async (id: number) => {
    const response = await http.delete(`/user/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUser(state) {
      state.user = undefined;
    },
    selectUser(state, action) {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = "idle";
        state.userList = action.payload;

        const textUsers = action.payload.filter(
          (x) => !!x.textEnabled && x.phoneNumber
        );
        state.textUserList = textUsers;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(addUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = action.payload;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(editUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = action.payload;
      })
      .addCase(editUser.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(getUser.pending, (state) => {
        state.formStatus = "loading";
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.formStatus = "idle";
        state.user = action.payload;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.formStatus = "failed";
      })
      .addCase(deleteUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.user = undefined;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = "failed";
      });
  },
});

export const { clearUser, selectUser } = userSlice.actions;

export default userSlice.reducer;
